<template lang="pug">
#support-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 新規お問い合わせ
    .box-body.register-body
      .register-row
        .register-group
          .message(v-html="supportDescription")
      FormInputSupportType(v-model="type" :planType="planType")
      FormInputSupportMethod(v-model="contactMethod" :planType="planType")
      FormTextarea(key-name="お問い合わせ内容" v-model="message" name="message"
        placeholder="お問い合わせ内容をご記載ください。"
        :validation-rules="{ required: true, max: 400 }" :maxlength="400" :styles="{ maxWidth: '600px', width: '600px', minHeight: '160px' }"
        required)

    .box-header.register-header
      .title
        span お客様情報
    .box-body.register-body
      .register-row
        .register-group
          .message
            span ご記入いただいたお客様情報へ回答をお送りさせていただきます。
      FormInput(key-name="氏名" v-model="name" name="name" type="text" placeholder="氏名" :maxlength="32"
        :validation-rules="{ required: true, max: 32 }" required)
      FormInput(key-name="氏名（かな）" v-model="kana" name="kana" type="text" placeholder="氏名（かな）" :maxlength="32"
        :validation-rules="{ required: true, max: 32, hiragana: true }" required)
      FormInput(v-if="telRequired" key-name="電話番号" v-model="tel" name="tel" type="text" placeholder="電話番号"
        :maxlength="13" :validation-rules="{ required: true, max: 13, tel: true }" :styles="{ width: '340px' }" required)
      FormInput(v-if="emailRequired" key-name="メールアドレス" v-model="email" name="email" type="text" placeholder="メールアドレス"
        :maxlength="100" :validation-rules="{ required: true, max: 100, email: true, equal_value: confirmEmail }" required)
      FormInput(v-if="confirmEmailRequired" key-name="メールアドレス（確認）" v-model="confirmEmail" name="confirm-email" type="text"
        placeholder="メールアドレス（確認）" :maxlength="100" :validation-rules="{ required: true, max: 100, email: true }" required)
      FormPrivacyCheck(v-model="confirm")

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(@click="showConfirmModal()" type="button") 送信
        button.btn.cancel-btn(@click="onCancel()" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="お問い合わせを登録しますか？" @ok="registerContactMessage")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="onCancel")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import CustomValidate from "@/commons/utils/CustomValidate";
import ContactsService from "@/commons/services/Contacts";
import ContactModel from "@/commons/models/Contact";
import { PlanType } from "@/commons/services/Plans";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputSupportType from "@/pc/components/forms/FormInputSupportType.vue";
import FormInputSupportMethod from "@/pc/components/forms/FormInputSupportMethod.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import FormPrivacyCheck from "@/pc/components/forms/FormPrivacyCheck.vue";

/**
 * お問い合わせメッセージ画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    FormInput,
    FormInputSupportType,
    FormInputSupportMethod,
    FormTextarea,
    FormPrivacyCheck
  },
  $_veeValidate: { validator: "new" }
})
export default class SupportRegister extends Vue {
  /** 問い合わせメッセージサービス */
  @Inject("ContactsService") contactsService!: ContactsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** プラン種別 */
  planType: PlanType = PlanType.UNKNOWN;

  /** お問い合わせの種類 */
  type: number = 0;
  /** 回答方法 */
  contactMethod: number = 0;
  /** 問い合わせ内容 */
  message: string = "";
  /** 担当者名 */
  name: string = "";
  /** 担当者名かな */
  kana: string = "";
  /** 電話番号 */
  tel: string = "";
  /** emailアドレス */
  email: string = "";
  /** emailアドレス(確認) */
  confirmEmail: string = "";
  /** 個人情報保護に同意する */
  confirm: boolean = false;
  /** 説明文 */
  supportDescription: string = Setting.support.registerDescription;

  get telRequired(): boolean {
    if (this.contactMethod == 0) {
      return false;
    } else if (this.contactMethod == 1) {
      return false;
    } else {
      return true;
    }
  }

  get emailRequired(): boolean {
    if (this.contactMethod == 0) {
      return false;
    } else if (this.contactMethod == 2) {
      return false;
    } else {
      return true;
    }
  }

  get confirmEmailRequired(): boolean {
    if (this.contactMethod == 0) {
      return false;
    } else if (this.contactMethod == 2) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * Vue Lifecycle created
   */
  public async created(): Promise<void> {
    CustomValidate.addCustomValidate(this.$validator);

    this.planType = userStore.facility.planType;

    // 入力項目の初期値を格納する
    const type: string = this.$route.params.type ? this.$route.params.type : "0";
    this.type = Number(type);

    this.name = userStore.loginUser.name;
    this.kana = userStore.loginUser.kana;
    this.tel = userStore.loginUser.tel;
    this.email = userStore.loginUser.mail;
    this.confirmEmail = userStore.loginUser.mail;

    return;
  }

  /**
   * 確認モーダルを表示する
   * バリデーションチェックでエラーの場合はモーダルを表示せずにエラーメッセージだけ表示する
   */
  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showValidationAlert = false;
      this.showRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * 問い合わせの登録
   */
  public async registerContactMessage(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const contact: any = this.createRegisterParam();
    await this.contactsService.post(userStore, contact);

    // 登録成功ダイアログを表示する
    this.showRegisterSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * 問い合わせ登録パラメータ作成
   */
  public createRegisterParam(): ContactModel {
    // 回答方法によっては入力不要なものがあるが、DBには登録したいという要望に対応する
    let customerTel: string = "";
    let customerMail: string = "";
    if (this.contactMethod == 0) {
      customerTel = userStore.loginUser.tel;
      customerMail = userStore.loginUser.mail;
    } else if (this.contactMethod == 1) {
      customerTel = userStore.loginUser.tel;
      customerMail = this.email;
    } else if (this.contactMethod == 2) {
      customerTel = this.tel;
      customerMail = userStore.loginUser.mail;
    } else {
      customerTel = this.tel;
      customerMail = this.email;
    }

    const contact: ContactModel = {
      facilityId: userStore.loginUser.facilityId,
      userId: userStore.loginUser.userId,
      type: this.type,
      contactMethod: this.contactMethod,
      customerName: this.name,
      customerKana: this.kana,
      customerTel: customerTel,
      customerMail: customerMail,
      deleteFlag: false,
      message: this.message
    };

    return contact;
  }

  /**
   * ### キャンセルボタン押下処理
   *
   * - 本画面は複数の遷移元が存在するので画面遷移ではなくブラウザバックする
   */
  public onCancel(): void {
    this.$router.go(-1);
    return;
  }
}
</script>

<style lang="scss" scoped>
#support-register {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
